import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAtomValue, useSetAtom } from 'jotai';
import { profileDataAtom } from '../../atoms/authAtoms';
import {
  Button,
  Dropdown,
  Field,
  Option,
  Spinner,
  Toolbar,
  ToolbarButton,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  ArrowLeft24Regular,
  Delete24Regular,
  PersonAdd24Regular,
  Save24Regular,
} from '@fluentui/react-icons';
import {
  FilterOperator,
  FilterValueInput,
  OPERATOR_CONFIG,
  OPERATORS_BY_TYPE,
  buildMetadataFilterColumn,
  getDefaultOperator,
  getDefaultValue,
  serializeFilterValue,
  parseFilterValueToArray,
  FILTERABLE_FIELD_TYPES,
} from '@moko/ui';
import {
  createAccessRule,
  getAccessRule,
  updateAccessRule,
  type AccessRuleEntity,
  type IAccessRuleMutation,
  type IAccessRulePermission,
} from '../../services/accessRuleService';
import {
  getEntitiesMetadatas,
  type IField,
  type ILookup,
} from '../../services/metadataService';
import UserOrGroupSelector, {
  type IPermissionItem,
} from '../../common/selectors/UsersOrGroupSelectors';
import { getUserOrGroupsNames } from '../../services/userService';
import { successMessageAtom } from '../../atoms/messageBarAtoms';

interface AccessRuleEditProps {
  entity: AccessRuleEntity;
}

const isMultiOperator = (op: FilterOperator): boolean =>
  op === FilterOperator.In || op === FilterOperator.NIn;

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  title: {
    margin: 0,
    paddingLeft: tokens.spacingHorizontalM,
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    maxWidth: '520px',
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
  },
  permissionsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
    maxWidth: '720px',
  },
  permissionsHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  permissionsTable: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  permissionsTh: {
    textAlign: 'left',
    fontWeight: tokens.fontWeightSemibold,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalS}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  permissionsTd: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalS}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    verticalAlign: 'middle',
  },
  permissionsEmpty: {
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
});

// Stable key per (user|group, id) pair — mirrors Status.tsx and is the same
// format expected by GET /a/usersOrGroupsNames.
const permKey = (item: IPermissionItem | IAccessRulePermission): string => {
  if ('isGroup' in item) {
    return `${item.isGroup ? 'G' : 'U'}${item.id}`;
  }
  if (item.userId != null) return `U${item.userId}`;
  if (item.groupId != null) return `G${item.groupId}`;
  return '';
};

const AccessRuleEdit = ({ entity }: AccessRuleEditProps) => {
  const styles = useStyles();
  const navigate = useNavigate();
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const profileData = useAtomValue(profileDataAtom);
  const [searchParams] = useSearchParams();
  const ruleIdParam = searchParams.get('id');
  const ruleId = ruleIdParam ? Number(ruleIdParam) : undefined;
  const isEdit = ruleId != null;

  const backUrl = `/security?tab=${entity.toLowerCase()}Accesses`;

  const [metadataNames, setMetadataNames] = useState<string[]>([]);
  const [fieldsByMetadata, setFieldsByMetadata] = useState<
    Record<string, IField[]>
  >({});
  const [lookups, setLookups] = useState<ILookup[]>([]);

  const [metadataName, setMetadataName] = useState<string>('');
  const [fieldName, setFieldName] = useState<string>('');
  const [operator, setOperator] = useState<FilterOperator>(FilterOperator.Eq);
  // FilterValueInput uses string for single-value ops, string[] for In/NIn.
  const [value, setValue] = useState<string | string[]>('');

  const [permissions, setPermissions] = useState<IPermissionItem[]>([]);
  const [ruleTenantId, setRuleTenantId] = useState<number | undefined>(
    undefined
  );

  const [loading, setLoading] = useState<boolean>(isEdit);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load metadata templates for the entity and (if editing) the existing rule.
  useEffect(() => {
    const abortController = new AbortController();
    (async () => {
      try {
        const resp = await getEntitiesMetadatas(abortController, [entity]);
        const names: string[] = [];
        const map: Record<string, IField[]> = {};
        const seenLookups = new Map<string, ILookup>();
        Object.entries(resp?.metadatas ?? {}).forEach(([key, value]) => {
          // Keys look like "Work|TemplateName". Take the name after the pipe;
          // fall back to the raw key for entities without a template.
          const sepIndex = key.indexOf('|');
          const name = sepIndex >= 0 ? key.slice(sepIndex + 1).trim() : key;
          if (!name) return;
          names.push(name);
          map[name] = value?.metadata?.fields ?? value?.fields ?? [];
          const lks = value?.metadata?.lookups ?? value?.lookups ?? [];
          lks.forEach((l) => {
            if (l?.name && !seenLookups.has(l.name)) seenLookups.set(l.name, l);
          });
        });
        setMetadataNames(names);
        setFieldsByMetadata(map);
        setLookups([...seenLookups.values()]);

        if (isEdit && ruleId != null) {
          const rule = await getAccessRule(abortController, entity, ruleId);
          // internalFetch resolves to `undefined` when the request was
          // aborted (e.g. StrictMode double-mount); skip silently in that
          // case — the second invocation will populate the form.
          if (!rule || abortController.signal.aborted) return;
          setMetadataName(rule.metadataName ?? '');
          setFieldName(rule.fieldName);
          const op = rule.operator;
          setOperator(op);
          setValue(
            isMultiOperator(op)
              ? parseFilterValueToArray(rule.fieldValue)
              : rule.fieldValue
          );
          if (rule.tenantId != null) setRuleTenantId(rule.tenantId);

          // Resolve permission ids → display names so the inline grid shows
          // user/group names instead of bare ids on first paint.
          const rawPerms = rule.permissions ?? [];
          const items: IPermissionItem[] = rawPerms
            .map((p) => {
              if (p.userId != null) return { id: p.userId, isGroup: false };
              if (p.groupId != null) return { id: p.groupId, isGroup: true };
              return null;
            })
            .filter((x): x is IPermissionItem => x !== null);
          if (items.length === 0) {
            setPermissions([]);
          } else {
            try {
              const ids = items.map(permKey);
              const names: Record<string, string> | undefined =
                await getUserOrGroupsNames(abortController, ids);
              if (abortController.signal.aborted) return;
              const lookup = new Map(Object.entries(names ?? {}));
              setPermissions(
                items.map((i) => {
                  const resolvedName =
                    lookup.get(permKey(i)) ??
                    `${i.isGroup ? 'Group' : 'User'} #${i.id}`;
                  return {
                    ...i,
                    name: resolvedName,
                    username: i.isGroup ? undefined : resolvedName,
                  };
                })
              );
            } catch {
              setPermissions(
                items.map((i) => ({
                  ...i,
                  name: `${i.isGroup ? 'Group' : 'User'} #${i.id}`,
                }))
              );
            }
          }
        }
      } catch (e) {
        if ((e as { name?: string })?.name !== 'AbortError') {
          setError((e as { message?: string })?.message ?? 'Failed to load.');
        }
      } finally {
        setLoading(false);
      }
    })();
    return () => abortController.abort();
  }, [entity, isEdit, ruleId]);

  // Union of fields across metadatas when no metadata is selected; otherwise
  // the fields of the chosen template. Filtered to types that support filtering.
  const availableFields: IField[] = useMemo(() => {
    const all = metadataName
      ? fieldsByMetadata[metadataName] ?? []
      : Object.values(fieldsByMetadata).flat();
    const seen = new Set<string>();
    const out: IField[] = [];
    for (const f of all) {
      if (!f?.name || !FILTERABLE_FIELD_TYPES.has(f.fieldType)) continue;
      if (seen.has(f.name)) continue;
      seen.add(f.name);
      out.push(f);
    }
    return out;
  }, [metadataName, fieldsByMetadata]);

  const selectedField = useMemo(
    () => availableFields.find((f) => f.name === fieldName),
    [availableFields, fieldName]
  );

  // Mirror grid filters: drive the operator + value UI off the same
  // IFilterableColumn shape so Lookup, Checkbox/RadioButton, Date, Number,
  // String all behave consistently across the two surfaces.
  const filterColumn = useMemo(
    () =>
      selectedField ? buildMetadataFilterColumn(selectedField, lookups) : null,
    [selectedField, lookups]
  );

  const availableOperators: FilterOperator[] = useMemo(() => {
    if (!filterColumn) return [];
    return filterColumn.operators ?? OPERATORS_BY_TYPE[filterColumn.type] ?? [];
  }, [filterColumn]);

  // If the selected operator is no longer valid for the new field, reset it
  // and the value (multi vs single shape may have changed too).
  useEffect(() => {
    if (!filterColumn) return;
    if (!availableOperators.includes(operator)) {
      const next = getDefaultOperator(filterColumn);
      setOperator(next);
      setValue(getDefaultValue(next));
    }
  }, [filterColumn, availableOperators, operator]);

  const handleSubmit = useCallback(
    async (e?: FormEvent) => {
      e?.preventDefault();
      setError(null);

      if (!fieldName) {
        setError('Field is required.');
        return;
      }
      if (permissions.length === 0) {
        setError('At least one user or group permission is required.');
        return;
      }

      const serialized = serializeFilterValue(value);

      // On edit, preserve the rule's original tenant. On create, use the
      // active tenant from the user profile (falls back to JWT tenant if 0).
      const effectiveTenantId = isEdit
        ? ruleTenantId
        : profileData.currentTenant || undefined;

      const payload: IAccessRuleMutation = {
        metadataName: metadataName || null,
        fieldName,
        operator,
        fieldValue: serialized,
        permissions: permissions.map<IAccessRulePermission>((p) => ({
          userId: p.isGroup ? null : p.id,
          groupId: p.isGroup ? p.id : null,
          tenantId: effectiveTenantId ?? null,
        })),
        tenantId: effectiveTenantId ?? null,
      };

      const abortController = new AbortController();
      setSaving(true);
      try {
        if (isEdit && ruleId != null) {
          await updateAccessRule(abortController, entity, ruleId, payload);
          setSuccessMessage(`${entity} access rule ${ruleId} updated.`);
        } else {
          const created = await createAccessRule(
            abortController,
            entity,
            payload
          );
          setSuccessMessage(`${entity} access rule ${created.ruleId} created.`);
        }
        navigate(backUrl);
      } catch (err) {
        setError((err as { message?: string })?.message ?? 'Save failed.');
      } finally {
        setSaving(false);
      }
    },
    [
      fieldName,
      permissions,
      operator,
      value,
      metadataName,
      isEdit,
      ruleId,
      ruleTenantId,
      profileData.currentTenant,
      entity,
      navigate,
      backUrl,
      setSuccessMessage,
    ]
  );

  if (loading) return <Spinner label="Loading…" />;

  return (
    <div className={styles.page}>
      <h2 className={styles.title}>
        {isEdit ? `Edit ${entity} access rule` : `New ${entity} access rule`}
      </h2>
      <Toolbar>
        <ToolbarButton
          icon={<ArrowLeft24Regular />}
          onClick={() => navigate(backUrl)}
        >
          Back
        </ToolbarButton>
        <ToolbarButton
          appearance="primary"
          icon={<Save24Regular />}
          onClick={() => handleSubmit()}
          disabled={saving}
        >
          {isEdit ? 'Update' : 'Save'}
        </ToolbarButton>
      </Toolbar>

      <form className={styles.form} onSubmit={handleSubmit}>
        <Field label="Metadata">
          <Dropdown
            placeholder="(All metadatas)"
            value={metadataName}
            selectedOptions={metadataName ? [metadataName] : []}
            onOptionSelect={(_, data) => {
              setMetadataName(data.optionValue ?? '');
              setFieldName('');
            }}
            clearable
          >
            <Option value="">(All metadatas)</Option>
            {metadataNames.map((n) => (
              <Option key={n} value={n}>
                {n}
              </Option>
            ))}
          </Dropdown>
        </Field>

        <Field label="Field" required>
          <Dropdown
            placeholder="Select a field"
            value={selectedField?.label || fieldName}
            selectedOptions={fieldName ? [fieldName] : []}
            onOptionSelect={(_, data) => setFieldName(data.optionValue ?? '')}
          >
            {availableFields.map((f) => (
              <Option key={f.name} value={f.name} text={f.label || f.name}>
                {f.label || f.name}
              </Option>
            ))}
          </Dropdown>
        </Field>

        <Field label="Operator" required>
          <Dropdown
            value={OPERATOR_CONFIG[operator]?.label ?? ''}
            selectedOptions={[operator]}
            disabled={!filterColumn}
            onOptionSelect={(_, data) => {
              const next =
                (data.optionValue as FilterOperator) ?? FilterOperator.Eq;
              setOperator(next);
              setValue(getDefaultValue(next));
            }}
          >
            {availableOperators.map((op) => (
              <Option key={op} value={op} text={OPERATOR_CONFIG[op].label}>
                {OPERATOR_CONFIG[op].label}
              </Option>
            ))}
          </Dropdown>
        </Field>

        {filterColumn && (
          <FilterValueInput
            column={filterColumn}
            operator={operator}
            value={value}
            onChange={setValue}
          />
        )}

        {error && (
          <span style={{ color: tokens.colorPaletteRedForeground1 }}>
            {error}
          </span>
        )}
      </form>

      <div className={styles.permissionsSection}>
        <div className={styles.permissionsHeader}>
          <strong>Permissions</strong>
          <Button
            icon={<PersonAdd24Regular />}
            onClick={() => setPickerOpen(true)}
          >
            Add user or group
          </Button>
        </div>
        {permissions.length === 0 ? (
          <span className={styles.permissionsEmpty}>
            No permissions yet — add at least one user or group.
          </span>
        ) : (
          <table className={styles.permissionsTable}>
            <thead>
              <tr>
                <th className={styles.permissionsTh}>User|Group Name</th>
                <th className={styles.permissionsTh}>User|Group Id</th>
                <th className={styles.permissionsTh}>IsGroup</th>
                <th className={styles.permissionsTh} style={{ width: 60 }} />
              </tr>
            </thead>
            <tbody>
              {permissions.map((p) => (
                <tr key={permKey(p)}>
                  <td className={styles.permissionsTd}>
                    {p.name ?? p.username ?? `#${p.id}`}
                  </td>
                  <td className={styles.permissionsTd}>
                    {p.id}{' '}
                    <small style={{ opacity: 0.7 }}>
                      ({p.isGroup ? 'Group' : 'User'})
                    </small>
                  </td>
                  <td className={styles.permissionsTd}>{String(p.isGroup)}</td>
                  <td className={styles.permissionsTd}>
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<Delete24Regular />}
                      aria-label="Remove permission"
                      title="Remove"
                      onClick={() =>
                        setPermissions((prev) =>
                          prev.filter(
                            (x) => !(x.id === p.id && x.isGroup === p.isGroup)
                          )
                        )
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <UserOrGroupSelector
        isOpen={pickerOpen}
        hideUsersAndGroupsSelector={() => setPickerOpen(false)}
        permissions={permissions}
        onUserOrGroupSelected={(id, isGroup, username, name) => {
          // The picker stays open so the user can pick several before
          // closing. De-dupe on (id, isGroup) — same key the panel uses.
          if (permissions.some((p) => p.id === id && p.isGroup === isGroup)) {
            return;
          }
          setPermissions((prev) => [
            ...prev,
            { id, isGroup, username, name: name ?? username },
          ]);
        }}
        onRemovePermission={(id) =>
          setPermissions((prev) => prev.filter((p) => p.id !== id))
        }
      />
    </div>
  );
};

export default AccessRuleEdit;
